import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Bell, Check, Trash2, ArrowLeft, Clock,
  AlertCircle, FileText, CheckCircle, Calendar
} from 'lucide-react';
import NavbarComptable from "../components/NavbarComptable";

const API_URL = 'https://api-ec.cinef-expertises.com/api/v1';

const NotificationsComptable = () => {
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });

  /* =======================
     LOAD NOTIFICATIONS
  ======================= */
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/notifications?filter=${filter}`);
      setNotifications(res.data.data);
    } catch (e) {
      console.error('Erreur chargement notifications', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  /* =======================
     ACTIONS
  ======================= */
  const markAsRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = async () => {
    await Promise.all(
      notifications.filter(n => !n.read).map(n =>
        api.patch(`/notifications/${n.id}/read`)
      )
    );
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = async (id) => {
    await api.delete(`/notifications/${id}`);
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const deleteAllRead = async () => {
    await Promise.all(
      notifications.filter(n => n.read).map(n =>
        api.delete(`/notifications/${n.id}`)
      )
    );
    setNotifications(notifications.filter(n => !n.read));
  };

  /* =======================
     UI HELPERS
  ======================= */
  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'deadline': return <Calendar size={20} />;
      case 'document': return <FileText size={20} />;
      case 'alert': return <AlertCircle size={20} />;
      case 'success': return <CheckCircle size={20} />;
      default: return <Bell size={20} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'deadline': return '#f59e0b';
      case 'document': return '#3b82f6';
      case 'alert': return '#ef4444';
      case 'success': return '#10b981';
      default: return '#6b7280';
    }
  };

  /* =======================
     RENDER
  ======================= */
  return (
    <>
    <NavbarComptable/>
      <div className="notifications-page">
        <div className="notifications-container">

          <div className="page-header">
            <button className="back-button" onClick={() => window.history.back()}>
              <ArrowLeft size={20} />
              <span>Tableau de bord</span>
            </button>
          </div>

          <div className="notifications-hero">
            <div className="hero-content">
              <div className="hero-icon"><Bell size={32} /></div>
              <div className="hero-text">
                <h1 className="page-title">Centre de Notifications</h1>
                <p className="page-subtitle">
                  {unreadCount > 0
                    ? `Vous avez ${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
                    : 'Vous êtes à jour'}
                </p>
              </div>
            </div>

            <div className="hero-actions">
              {unreadCount > 0 && (
                <button className="action-btn primary" onClick={markAllAsRead}>
                  <Check size={18} /> Tout marquer comme lu
                </button>
              )}
              <button className="action-btn secondary" onClick={deleteAllRead}>
                <Trash2 size={18} /> Supprimer les lues
              </button>
            </div>
          </div>

          <div className="filters-bar">
            {['all', 'unread', 'read'].map(f => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'Toutes' : f === 'unread' ? 'Non lues' : 'Lues'}
              </button>
            ))}
          </div>

          <div className="notifications-list">
            {loading ? (
              <p>Chargement...</p>
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <Bell size={48} />
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map(notif => (
                <div key={notif.id} className={`notification-item ${!notif.read ? 'unread' : ''}`}>
                  <div
                    className="notification-icon"
                    style={{
                      background: `${getNotificationColor(notif.type)}15`,
                      color: getNotificationColor(notif.type)
                    }}
                  >
                    {getNotificationIcon(notif.type)}
                  </div>

                  <div className="notification-content">
                    <h3>{notif.title}</h3>
                    <p>{notif.message}</p>
                    <div className="notification-time">
                      <Clock size={14} /> {notif.time}
                    </div>
                  </div>

                  <div className="notification-actions">
                    {!notif.read && (
                      <button onClick={() => markAsRead(notif.id)}>
                        <Check size={18} />
                      </button>
                    )}
                    <button onClick={() => deleteNotification(notif.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .notifications-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
          padding: 32px 24px;
        }

        .notifications-container {
          max-width: 1000px;
          margin: 0 auto;
        }

        /* Page Header */
        .page-header {
          margin-bottom: 32px;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .back-button:hover {
          background: #f9fafb;
          border-color: #008A44;
          color: #008A44;
          transform: translateX(-2px);
          box-shadow: 0 4px 12px rgba(0, 138, 68, 0.1);
        }

        /* Hero Section */
        .notifications-hero {
          background: linear-gradient(135deg, #008A44 0%, #006633 100%);
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 32px;
          box-shadow: 0 10px 40px rgba(0, 138, 68, 0.2);
          position: relative;
          overflow: hidden;
        }

        .notifications-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
          border-radius: 50%;
        }

        .hero-content {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
        }

        .hero-icon {
          width: 64px;
          height: 64px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .hero-text {
          flex: 1;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          color: white;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        .hero-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          position: relative;
          z-index: 1;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.primary {
          background: white;
          color: #008A44;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .action-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        .action-btn.secondary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
        }

        .action-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        /* Filters Section */
        .filters-section {
          margin-bottom: 24px;
        }

        .filters-bar {
          display: flex;
          gap: 12px;
          background: white;
          padding: 8px;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        }

        .filter-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          background: transparent;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .filter-btn:hover {
          background: #f9fafb;
          color: #374151;
        }

        .filter-btn.active {
          background: linear-gradient(135deg, #008A44 0%, #006633 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(0, 138, 68, 0.2);
        }

        .filter-label {
          font-weight: 600;
        }

        .filter-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          padding: 0 8px;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          font-size: 12px;
          font-weight: 700;
        }

        .filter-btn.active .filter-count {
          background: rgba(255, 255, 255, 0.25);
        }

        /* Notifications List */
        .notifications-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .notification-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 24px;
          background: white;
          border: 2px solid transparent;
          border-radius: 16px;
          transition: all 0.3s ease;
          position: relative;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .notification-item:hover {
          border-color: #008A44;
          box-shadow: 0 8px 24px rgba(0, 138, 68, 0.12);
          transform: translateY(-2px);
        }

        .notification-item.unread {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-color: #86efac;
        }

        .notification-item.unread:hover {
          border-color: #008A44;
        }

        .notification-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .notification-title {
          font-size: 17px;
          font-weight: 700;
          color: #111827;
          letter-spacing: -0.2px;
        }

        .priority-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .unread-indicator {
          width: 10px;
          height: 10px;
          background: #008A44;
          border-radius: 50%;
          flex-shrink: 0;
          box-shadow: 0 0 0 4px rgba(0, 138, 68, 0.2);
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(0, 138, 68, 0.2);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(0, 138, 68, 0.1);
          }
        }

        .notification-message {
          font-size: 15px;
          color: #4b5563;
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .notification-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .notification-time {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #9ca3af;
          font-weight: 500;
        }

        .notification-actions {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }

        .action-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-icon.read {
          background: #f0fdf4;
          color: #008A44;
        }

        .action-icon.read:hover {
          background: #dcfce7;
          transform: scale(1.05);
        }

        .action-icon.delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-icon.delete:hover {
          background: #fee2e2;
          transform: scale(1.05);
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 80px 20px;
          text-align: center;
          background: white;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .empty-icon {
          width: 96px;
          height: 96px;
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          margin-bottom: 24px;
        }

        .empty-state h3 {
          font-size: 20px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 8px;
        }

        .empty-state p {
          font-size: 15px;
          color: #6b7280;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .notifications-page {
            padding: 20px 16px;
          }

          .page-header {
            margin-bottom: 24px;
          }

          .notifications-hero {
            padding: 24px;
            border-radius: 16px;
          }

          .hero-content {
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
          }

          .hero-icon {
            width: 56px;
            height: 56px;
          }

          .page-title {
            font-size: 26px;
          }

          .page-subtitle {
            font-size: 14px;
          }

          .hero-actions {
            flex-direction: column;
            width: 100%;
          }

          .action-btn {
            justify-content: center;
            width: 100%;
          }

          .filters-bar {
            flex-direction: column;
            gap: 8px;
          }

          .filter-btn {
            width: 100%;
            justify-content: space-between;
          }

          .notification-item {
            padding: 20px;
          }

          .notification-icon {
            width: 40px;
            height: 40px;
          }

          .notification-actions {
            position: absolute;
            top: 20px;
            right: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default NotificationsComptable;